import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { Button } from "@/components/ui/button";
import { Loader2, Music4, Headphones } from "lucide-react";

const COVER = "https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

export default function Meditations() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/content/meditations").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
      <div className="max-w-2xl mb-16">
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Inner Genie Meditations</span>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3 mb-4">A library to tune your inner frequency.</h1>
        <p className="text-muted-foreground leading-relaxed">Streamed sound-healing meditations, tagged by the area you want to nurture. Press play and listen right here.</p>
      </div>

      {items === null ? (
        <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-primary" strokeWidth={1.5} /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-16 text-center" data-testid="meditations-empty">
          <span className="grid place-items-center h-14 w-14 rounded-full bg-secondary text-primary mx-auto mb-5"><Headphones strokeWidth={1.5} /></span>
          <h3 className="font-serif text-2xl mb-2">Meditations are on their way</h3>
          <p className="text-muted-foreground text-sm max-w-md mx-auto mb-6">Our team is curating the Inner Genie library. In the meantime, take your free voice scan.</p>
          <Link to="/register"><Button className="rounded-full bg-primary text-primary-foreground font-semibold">Take a free scan</Button></Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="meditations-list">
          {items.map((m, i) => (
            <div key={m.id} className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col" data-testid={`meditation-${i}`}>
              <div className="relative h-44 overflow-hidden">
                <img src={m.image_url ? mediaUrl(m.image_url) : COVER} alt={m.title} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/30 to-transparent" />
                <span className="absolute left-5 bottom-4 grid place-items-center h-11 w-11 rounded-full bg-primary/90 text-primary-foreground shadow-lg"><Music4 size={20} strokeWidth={1.5} /></span>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <h3 className="font-serif text-xl mb-1">{m.title}</h3>
                {m.description && <p className="text-muted-foreground text-sm leading-relaxed mb-3">{m.description}</p>}
                {m.tags && (
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {String(m.tags).split(",").map((t) => t.trim()).filter(Boolean).map((t) => (
                      <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border">{t}</span>
                    ))}
                  </div>
                )}
                <div className="mt-auto">
                  {m.audio_url ? (
                    <audio controls src={mediaUrl(m.audio_url)} className="w-full h-10" data-testid={`meditation-audio-${i}`} />
                  ) : <p className="text-xs text-muted-foreground">Audio coming soon.</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
